import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/uiDir/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/uiDir/avatar";
import { Button } from "@/components/uiDir/button";
import { UserPlus, UserCheck, Loader2, TrendingUp, Sparkles } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { toast } from "sonner";
import type { SuggestedUser, SuggestedUsersProps } from "@/lib/models/suggestedusersModel";
export const SuggestedUsers = ({ isCompact = false }: SuggestedUsersProps) => {
  const [suggestedUsers, setSuggestedUsers] = useState<SuggestedUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [followingStates, setFollowingStates] = useState<Record<string, boolean>>({});
  const [processingFollows, setProcessingFollows] = useState<Record<string, boolean>>({});
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  const fetchGeneralSuggestions = async () => {
    try {
      setIsLoading(true);
      const { data: users, error } = await supabase
        .from("profiles")
        .select("id, full_name, username, program, avatar_url, title, bio, projects_count, followers_count, total_likes")
        .order("followers_count", { ascending: false })
        .limit(6);

      if (error) throw error;

      const usersWithFollowStatus = (users || []).map(u => ({ ...u, is_following: false }));

      setSuggestedUsers(usersWithFollowStatus);
      const initialFollowStates: Record<string, boolean> = {};
      usersWithFollowStatus.forEach(u => (initialFollowStates[u.id] = false));
      setFollowingStates(initialFollowStates);
    } catch (err) {
      console.error("Error fetching general suggestions:", err);
      toast.error("Failed to load suggestions");
    } finally {
      setIsLoading(false);
    }
  };

 const fetchSuggestedUsers = async (userId: string) => {
  try {
    setIsLoading(true);

    const { data: followingData } = await supabase
      .from("follows")
      .select("following_id")
      .eq("follower_id", userId);

    const followingIds = followingData?.map(f => f.following_id) || [];

    const { data: currentUserProfile } = await supabase
      .from("profiles")
      .select("program")
      .eq("id", userId)
      .single();

    const currentProgram = currentUserProfile?.program;

    const { data: users, error } = await supabase
      .from("profiles")
      .select("id, full_name, username, program, avatar_url, title, bio, projects_count, followers_count, total_likes")
      .neq("id", userId)
      .limit(50);

    if (error) throw error;

    const filteredUsers = (users || []).filter(u => !followingIds.includes(u.id));

    const scoredUsers = filteredUsers.map(u => {
      let score = 0;
      if (u.program === currentProgram) score += 50;
      score += u.projects_count * 5;
      score += u.followers_count * 2;
      score += u.total_likes;
      return { ...u, score };
    });

    const topUsers = scoredUsers
      .sort((a, b) => b.score - a.score)
      .slice(0, 6)
      .map(({ score, ...rest }) => ({ ...rest, is_following: false }));

    if (topUsers.length > 0) {
      setSuggestedUsers(topUsers);
      const states: Record<string, boolean> = {};
      topUsers.forEach(u => (states[u.id] = false));
      setFollowingStates(states);
    } else {
      setSuggestedUsers([]);
    }
  } catch (err) {
    console.error("Error fetching suggested users:", err);
    toast.error("Failed to load suggestions");
  } finally {
    setIsLoading(false);
  }
};

  useEffect(() => {
    let isMounted = true;

    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        if (!isMounted) return;

        const user = session?.user;
        setCurrentUserId(user?.id ?? null);

        if (user) {
          await fetchSuggestedUsers(user.id);
        } else {
          await fetchGeneralSuggestions();
        }
      }
    );

    return () => {
      isMounted = false;
      authListener.subscription.unsubscribe();
    };
  }, []);

  const handleFollowToggle = async (userId: string) => {
    if (!currentUserId) {
      toast.error('Please log in to follow users');
      return;
    }

    // Prevent multiple clicks
    if (processingFollows[userId]) return;

    const isCurrentlyFollowing = followingStates[userId];

    try {
      // Mark as processing
      setProcessingFollows(prev => ({ ...prev, [userId]: true }));

      if (isCurrentlyFollowing) {
        // Unfollow
        const { error } = await supabase
          .from('follows')
          .delete()
          .eq('follower_id', currentUserId)
          .eq('following_id', userId);

        if (error) throw error;
        
        setFollowingStates(prev => ({ ...prev, [userId]: false }));
        
        // Update local followers count
        setSuggestedUsers(prev => 
          prev.map(user => 
            user.id === userId 
              ? { ...user, followers_count: Math.max(0, user.followers_count - 1) }
              : user
          )
        );

        toast.success('Unfollowed successfully');
      } else {
        // Follow
        const { error } = await supabase
          .from('follows')
          .insert({
            follower_id: currentUserId,
            following_id: userId
          });

        if (error) throw error;

        setFollowingStates(prev => ({ ...prev, [userId]: true }));
        
        // Update local followers count
        setSuggestedUsers(prev => 
          prev.map(user => 
            user.id === userId 
              ? { ...user, followers_count: user.followers_count + 1 }
              : user
          )
        );

        toast.success('Following successfully');
      }

    } catch (err) {
      console.error('Error toggling follow:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to update follow status';
      toast.error(errorMessage);
    } finally {
      // Remove processing state
      setProcessingFollows(prev => ({ ...prev, [userId]: false }));
    }
  };

  const getProgramLabel = (program: string): string => {
    const labels: Record<string, string> = {
      'COMSCI': 'Computer Science',
      'BMMA': 'BMMA',
      'FILM': 'Film',
      'INFOSYSTEM': 'Info Systems',
      'ENTREPRENEURSHIP': 'Entrepreneurship',
      'DESIGN': 'Design',
      'ARCHITECTURE': 'Architecture'
    };
    return labels[program] || program;
  };

  const getProgramColor = (program: string): string => {
    const colors: Record<string, string> = {
      'COMSCI': 'bg-blue-100 text-blue-700',
      'BMMA': 'bg-purple-100 text-purple-700',
      'FILM': 'bg-red-100 text-red-700',
      'INFOSYSTEM': 'bg-green-100 text-green-700',
      'ENTREPRENEURSHIP': 'bg-yellow-100 text-yellow-700',
      'DESIGN': 'bg-pink-100 text-pink-700',
      'ARCHITECTURE': 'bg-orange-100 text-orange-700'
    };
    return colors[program] || 'bg-gray-100 text-gray-700';
  };

  if (isLoading) {
    return (
      <Card className="shadow-lg border-0 rounded-2xl bg-white">
        {/* <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <CardTitle className="text-lg font-bold text-primary-900">Suggested For You</CardTitle>
          </div>
        </CardHeader> */}
        <CardContent className="flex items-center justify-center py-12">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Finding amazing creators...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (suggestedUsers.length === 0) {
    return (
      <Card className="shadow-lg border-0 rounded-2xl bg-white">
        {/* <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <CardTitle className="text-lg font-bold text-primary-900">Suggested For You</CardTitle>
          </div>
        </CardHeader> */}
        <CardContent className="py-8 text-center">
          <div className="space-y-2">
            <p className="text-sm text-gray-600">No suggestions available</p>
            <p className="text-xs text-gray-500">Check back later for new creators!</p>
          </div>
        </CardContent>
      </Card>
    );
  }

if (isCompact) {
  return (
    <div className="space-y-3">
      {suggestedUsers.map((user) => (
        <div 
          key={user.id} 
          className="flex items-start gap-3 p-3 rounded-xl hover:bg-blue-50 transition-colors group"
        >
          <Link to={`/profile/${user.username}`}>
            <Avatar className="h-10 w-10 border-2 border-blue-200 group-hover:border-blue-500 transition-colors cursor-pointer flex-shrink-0">
              <AvatarImage 
                src={user.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.username}`} 
              />
              <AvatarFallback className="bg-blue-100 text-blue-700 font-semibold text-sm">
                {user.full_name.split(' ').map(n => n[0]).join('')}
              </AvatarFallback>
            </Avatar>
          </Link>
          
          <div className="flex-1 min-w-0">
            <Link to={`/profile/${user.username}`} className="block">
              <p className="font-semibold text-sm text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                {user.full_name}
              </p>
              <p className="text-xs text-gray-500 truncate">@{user.username}</p>
            </Link>
{/*             
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getProgramColor(user.program)}`}>
                {getProgramLabel(user.program)}
              </span>
              <span className="text-xs text-gray-500">
                {user.projects_count} {user.projects_count === 1 ? 'project' : 'projects'}
              </span>
              <span className="text-xs text-gray-400">•</span>
              <span className="text-xs text-gray-500">
                {user.followers_count} {user.followers_count === 1 ? 'follower' : 'followers'}
              </span>
            </div> */}
          </div>
          
          <Button 
            size="sm" 
            variant={followingStates[user.id] ? "secondary" : "default"}
            className={`flex-shrink-0 px-2 py-1 text-xs ${
              followingStates[user.id] 
                ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' 
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            } transition-colors`}
            onClick={() => handleFollowToggle(user.id)}
            disabled={processingFollows[user.id] || !currentUserId}
          >
            {processingFollows[user.id] ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : followingStates[user.id] ? (
              <UserCheck className="h-3 w-3" />
            ) : (
              <UserPlus className="h-3 w-3" />
            )}
          </Button>
        </div>
      ))}

      <Link to="/explore" className="block">
        <Button 
          variant="ghost" 
          className="w-full hover:bg-blue-50 hover:text-blue-600 text-blue-600 font-medium mt-2"
        >
          View All Suggestions
        </Button>
      </Link>
    </div>
  );
}

};